import type { SiteEvidenceDocument } from '../../types/siteEvidence';

type SolarResourceSed = Partial<Pick<SiteEvidenceDocument, 'solar'>>;

/** Detect only denial of recorded irradiance evidence. A positive resource
 * value is not evidence of a PV design, generation yield or firm capacity. */
export function solarResourcePresenceFindings(text: string, sed: SolarResourceSed): Array<{
  excerpt: string; expected: string; sourcePaths: string[];
}> {
  const section = sed.solar;
  if (section?.status !== 'verified' || !section.data) return [];
  const available = (['ghi', 'dni'] as const).filter(metric =>
    typeof section.data![metric] === 'number'
    && Number.isFinite(section.data![metric]) && section.data![metric] > 0);
  if (!available.length) return [];

  const rows: Array<{ excerpt: string; expected: string; sourcePaths: string[] }> = [];
  const clauses = text.split(/(?<=[.!?])(?<!\bU\.S\.)\s+|\n+|;\s*|,?\s+(?:but|although|whereas|while)\s+/i)
    .flatMap(clause => {
      // Keep a shared subject ("GHI and DNI are missing") together, but
      // separate two predicates ("GHI is available and DNI is missing").
      const split = /\s+and\s+(?=(?:the\s+)?(?:GHI|DNI|global horizontal irradiance|direct normal irradiance)\b)/i.exec(clause);
      return split && /\b(?:is|are|was|were|remains?)\b/i.test(clause.slice(0, split.index))
        ? [clause.slice(0, split.index), clause.slice(split.index + split[0].length)] : [clause];
    });
  for (const clause of clauses) {
    const statement = clause.trim();
    if (!/\b(?:GHI|DNI|global horizontal irradi(?:ance|ation)|direct normal irradi(?:ance|ation)|solar[- ](?:resource|irradiance|irradiation|potential|data|information))\b/i.test(statement)) continue;
    const otherProject = /\b(?:peer|comparable|comparison|other[- ]project|another (?:site|project|facility))\b/i.test(statement);
    if (otherProject && !/\b(?:this|our|subject)\s+(?:site|project|campus|facility)\b/i.test(statement)) continue;
    if (/\b(?:if|whether|incorrect|false|not true|do not claim|does not mean|not missing|not absent|not unavailable)\b/i.test(statement)
      || statement.endsWith('?')) continue;
    // The frozen metrics are resource screening only. These separate details
    // may remain unknown even when a scalar GHI or DNI has been verified.
    if (/\b(?:PV|photovoltaic|panels?|arrays?|layout|design|installed capacity|capacity factor|yield|generation|output|firming|firm capacity|storage|batter(?:y|ies)|feasibility|viability|shading|losses|tilt|azimuth|hourly|monthly|seasonal|diurnal|time[- ]series|daily profile|on[- ]site measured|site[- ]measured|measurements?)\b/i.test(statement)) continue;
    const absent = /\b(?:is|are|was|were|remains?)\s+(?:unavailable|absent|missing|not\s+(?:provided|available|recorded|included|detailed|documented|reported))\b|\b(?:has|have)\s+not\s+been\s+(?:provided|recorded|included|documented|reported)\b|\b(?:does|do)\s+not\s+(?:contain|include|provide|record|detail|report)\b|\b(?:SED|record|evidence|documentation)\s+lacks?\b|\bno\s+(?:(?:recorded|modeled|modelled|solar[- ]resource|solar|resource|irradiance|irradiation|GHI|DNI)\s+){0,6}(?:data|information|values?|estimates?)\b/i.test(statement);
    if (!absent) continue;
    const named = available.filter(metric => metric === 'ghi'
      ? /\bGHI\b|\bglobal horizontal irradi(?:ance|ation)\b/i.test(statement)
      : /\bDNI\b|\bdirect normal irradi(?:ance|ation)\b/i.test(statement));
    const namesSpecificMetric = /\b(?:GHI|DNI|global horizontal irradi(?:ance|ation)|direct normal irradi(?:ance|ation)|DHI|diffuse horizontal irradi(?:ance|ation))\b/i.test(statement);
    const recorded = namesSpecificMetric ? named : available;
    if (!recorded.length) continue;
    const values = recorded.map(metric => `${metric.toUpperCase()} ${section.data![metric]} kWh/m²/day`).join('; ');
    rows.push({
      excerpt: statement,
      expected: `Recorded solar-resource screening is available: ${values}. These irradiance metrics do not establish a PV design, installed capacity, hourly generation, firm output or project feasibility; those require separate analysis.`,
      sourcePaths: recorded.map(metric => `solar.data.${metric}`),
    });
  }
  return rows;
}
